import * as React from 'react';
import * as Swipeable from 'react-swipeable';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import { action, computed, observable } from 'mobx';
import { ISelectedImage } from '../../interface/component/selected-image.interface';
import { IInjectedProps } from '../../interface/component/injected-props.interface';

const IndicatorsWrapper = styled.div`
  width: 80%;
  position: absolute;
  display: flex;
  bottom: 20px;
  justify-content: center;
`;

interface ICauroselProps {
  imageCount: number;
  transitionDuration: number;
}

const CarouselWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: block;
  white-space: nowrap;
  will-change: transform;
  transform: translate3d(-${(props: ICauroselProps) => (props.imageCount - 1) * 100}%, 0px, 0px);
  transition: transform ${(props: ICauroselProps) => props.transitionDuration}s;
`;

const Indicator = styled.div`
  width: ${20}px;
  height: ${20}px;
  border-radius: 50%;
  background-color: ${(props: { color: string }) => props.color};
  margin: 10px;
  cursor: pointer;
`;

export interface ICarousel {
  images: React.ReactNode[];
  wrapper: React.ComponentClass;
  transitionDuration: number;
  transitionTime: number;
  disableIndicators?: boolean;
  autoLoop: boolean;
}

@inject('state')
@observer
export class Carousel extends React.Component<ICarousel & IInjectedProps> {
  @observable
  slideShowTimeout: any;
  @observable
  previousSlide: number;
  @observable
  currentSlide: number = 0;
  @observable
  isSliding: boolean = false;

  getIndicatorColor = (index) => {
    return index === this.currentSlide ? this.props.state.blockColors.SecondaryTextColor : this.props.state.blockColors.SecondaryBackgroundColor;
  }

  @computed
  get images() {
    return this.props.images;
  }

  @computed
  get indicators() {
    return this.props.images.map((image: ISelectedImage, index) => {
      return <Indicator
        key={index}
        color={this.getIndicatorColor(index)}
        onClick={action(() => {
          this.clearTimeout();
          this.previousSlide = this.currentSlide;
          this.currentSlide = index % this.props.images.length;
          this.isSliding = false;
        })}
      />;
    });
  }

  componentDidMount() {
    this.createTimeout(this.props);
  }

  slideForward = (props: any) => action(() => {
    if (!props) {
      props = this.props;
    }
    if (props.images.length <= 1) {
      return;
    }
    this.previousSlide = this.currentSlide;
    this.currentSlide = (this.currentSlide + 1) % props.images.length;
    this.isSliding = false;
    this.clearTimeout();
  })

  slideBackward = (props?: any) => action(() => {
    if (!props) {
      props = this.props;
    }
    if (props.images.length <= 1) {
      return;
    }
    this.previousSlide = this.currentSlide;
    let toSlide = this.currentSlide - 1;
    if (toSlide < 0) {
      toSlide = props.images.length + toSlide;
    }
    this.currentSlide = toSlide;
    this.isSliding = false;
    this.clearTimeout();
  })

  createTimeout = (nextProps) => {
    if (nextProps.images.length > 1 && nextProps.autoLoop) {
      this.slideShowTimeout = setTimeout(this.slideForward(nextProps), nextProps.transitionTime * 1000);
    }
  }

  @action
  clearState() {
    this.previousSlide = undefined;
    this.currentSlide = 0;
    this.clearTimeout();
  }

  clearTimeout() {
    if (this.slideShowTimeout) {
      clearTimeout(this.slideShowTimeout);
      this.slideShowTimeout = undefined;
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.images.length !== this.props.images.length) {
      this.clearState();
      this.createTimeout(nextProps);
    }
  }

  componentWillUnmount() {
    this.clearState();
  }

  componentDidUpdate() {
    if (!this.isSliding && this.props.autoLoop) {
      setTimeout(action(() => {
        if (!this.isSliding && !this.slideShowTimeout && this.props.autoLoop) {
          this.isSliding = true;
          this.createTimeout(this.props);
        }
      }), 100);
    }
  }

  render() {
    const images = this.images;
    const Wrapper = this.props.wrapper;
    return (
      <Swipeable
        onSwipedLeft={this.slideForward(this.props)}
        onSwipedRight={this.slideBackward(this.props)}
      >
        <Wrapper>
          <CarouselWrapper
            transitionDuration={this.props.transitionDuration}
            imageCount={this.currentSlide + 1}
          >
            {images}
          </CarouselWrapper>
          {(this.props.images.length > 1 && !this.props.disableIndicators) ? <IndicatorsWrapper>
            {this.indicators}
          </IndicatorsWrapper> : undefined}
        </Wrapper>
      </Swipeable>
    );
  }
}
